/*
 * cart-banner-upsell.js
 *
 * <cart-banner-upsell> — an auto-selected cart upsell rendered as a banner.
 *
 * Unlike the toggle upsells (cart-drawer-upsell + upsell-block.liquid), this
 * banner has no on/off control: when the cart is eligible it silently adds one
 * unit of the configured variant, and the ONLY way to opt out is the X on the
 * banner. It is deliberately a self-contained element, in the mould of
 * <shipping-protection-toggle>, but it borrows the theme's `_upsell` line-item
 * marker so its cart line stays distinct from a line the shopper added on their
 * own:
 *
 *   - Add posts `properties[_upsell] = <upsellKey>`. Underscore-prefixed
 *     properties are hidden from the cart/checkout/order UI, and Shopify keeps a
 *     line WITH properties separate from an identical line WITHOUT them, so the
 *     two coexist. cart-drawer_items.liquid / main-cart-items.liquid turn that
 *     property into a `cart-item--upsell-<key>` class.
 *   - The snippet hides that class inside the drawer (the banner IS the line's
 *     surface there), so the shopper sees the banner, not a duplicate line item.
 *   - Removal fetches /cart.js, finds the line whose `_upsell` property equals
 *     this banner's key, and zeroes THAT line by its unique line key — never a
 *     line the shopper added with the same variant.
 *
 * This element is NOT a <cart-drawer-upsell>, so the CartDrawerUpsell.prototype
 * patches in cart-upsell-scope.js never touch it. The generic
 * `.cart-item--upsell-` accounting in that file (clear-cart / one-real-product)
 * still counts the banner's line as an upsell, which is exactly right.
 *
 * State model
 *   - "added"   : the managed line exists (or is being added optimistically).
 *   - "removed" : the shopper clicked X. Remembered per cart in localStorage,
 *                 keyed by the cart token so a brand-new cart resets eligibility.
 *                 Renders the optional Undo row, or nothing.
 *   - hidden    : cart has no real (non-upsell) item, or the variant is
 *                 unconfigured/unavailable — never add to an empty cart.
 *
 * Design-mode (theme editor) never mutates a real cart: the X and Undo only
 * flip the local preview.
 */
(function () {
  if (!window.customElements) return;
  if (customElements.get('cart-banner-upsell')) return;

  var ROUTES = window.routes || {};
  var CART_ADD = (ROUTES.cart_add_url || '/cart/add');
  var CART_CHANGE = (ROUTES.cart_change_url || '/cart/change');
  var CART_URL = (ROUTES.cart_url || '/cart');

  /* One add per (key, cart) may be in flight at a time — guards against a
     double auto-add when the drawer re-renders while the first add is pending,
     and against rapid Undo clicks. Module scope so it spans element instances
     (each cart re-render destroys and recreates the element). */
  var inFlight = {};

  function cartToken() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) {
      return '';
    }
  }

  function storeKey(key) {
    return 'cbu:' + key;
  }

  /* Dismissal is scoped to the current cart token. A different (or absent)
     token — i.e. a new cart after the old one was emptied — reads as
     not-dismissed, so eligibility resets automatically. */
  function isDismissed(key, token) {
    try {
      var raw = localStorage.getItem(storeKey(key));
      if (!raw) return false;
      var parsed = JSON.parse(raw);
      return !!parsed && parsed.token === token && parsed.dismissed === true;
    } catch (e) {
      return false;
    }
  }

  function setDismissed(key, token, dismissed) {
    try {
      localStorage.setItem(
        storeKey(key),
        JSON.stringify({ token: token, dismissed: !!dismissed })
      );
    } catch (e) {
      /* private mode / quota — dismissal just won't persist across reloads. */
    }
  }

  class CartBannerUpsell extends HTMLElement {
    connectedCallback() {
      this.upsellKey = this.dataset.upsellKey;
      this.variantId = parseInt(this.dataset.variantId, 10);
      this.orderTag = this.dataset.orderTag || '';
      this.showRemovedMessage = this.dataset.showRemovedMessage === 'true';

      this.banner = this.querySelector('.cbu__banner');
      this.removedRow = this.querySelector('.cbu__removed');
      this.errorEl = this.querySelector('.cbu__error');
      this.removeBtn = this.querySelector('.cbu__remove');
      this.undoBtn = this.querySelector('.cbu__undo');
      this.busy = false;

      /* Theme editor: never hit /cart. Keep the X/Undo as a local preview. */
      if (window.Shopify && window.Shopify.designMode) {
        if (this.removeBtn) {
          this.removeBtn.addEventListener('click', this.showRemoved.bind(this));
        }
        if (this.undoBtn) {
          this.undoBtn.addEventListener('click', this.showAdded.bind(this));
        }
        return;
      }

      /* Unconfigured / unavailable variant — render nothing, add nothing. */
      if (!this.variantId) {
        this.hideAll();
        return;
      }

      if (this.removeBtn) {
        this.removeBtn.addEventListener('click', this.handleRemove.bind(this));
      }
      if (this.undoBtn) {
        this.undoBtn.addEventListener('click', this.handleUndo.bind(this));
      }

      this.resolveState();
    }

    get drawer() {
      return this.closest('cart-drawer') || document.querySelector('cart-drawer');
    }

    ownLine() {
      var scope = this.drawer || document;
      return scope.querySelector('.cart-item--upsell-' + this.upsellKey);
    }

    /* At least one line the shopper (or another upsell) put there — i.e. not
       this banner's own marked line. Read from the rendered cart, so it is
       correct even when `is-empty` is stale on the drawer host. */
    cartHasRealItem() {
      var scope = this.drawer || document;
      var items = scope.querySelectorAll('.cart-item');
      for (var i = 0; i < items.length; i++) {
        if (items[i].className.indexOf('cart-item--upsell-') === -1) return true;
      }
      return false;
    }

    resolveState() {
      if (this.dataset.inCart === 'true' || this.ownLine()) {
        this.showAdded();
        return;
      }
      if (!this.cartHasRealItem()) {
        this.hideAll();
        return;
      }
      if (isDismissed(this.upsellKey, cartToken())) {
        this.showRemoved();
        return;
      }
      this.addUpsell(true);
    }

    sectionsToRender() {
      var drawer = this.drawer;
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        return drawer.getSectionsToRender().map(function (s) {
          return s.id;
        });
      }
      return ['cart-drawer', 'cart-icon-bubble'];
    }

    refresh(data) {
      var drawer = this.drawer;
      if (drawer && typeof drawer.renderContents === 'function' && data && data.sections) {
        drawer.renderContents(data, true);
        /* `is-empty` on the <cart-drawer> host is stale after renderContents;
           clear it so a first add out of an empty cart shows the lines. */
        drawer.classList.remove('is-empty');
        return;
      }
      /* No drawer to patch (e.g. an isolated cart page) — reload so the
         confirmed cart state is shown rather than a stale optimistic one. */
      window.location.reload();
    }

    addUpsell(isAuto) {
      var token = cartToken();
      var lock = this.upsellKey + '@' + token;
      if (this.busy || inFlight[lock]) return;
      if (this.ownLine()) {
        this.showAdded();
        return;
      }
      if (!this.cartHasRealItem()) {
        this.hideAll();
        return;
      }

      this.busy = true;
      inFlight[lock] = true;
      this.hideError();
      this.showAdded(); /* optimistic; the confirmed re-render replaces it */

      var properties = { _upsell: this.upsellKey };
      if (this.orderTag) properties._upsell_tag = this.orderTag;

      var self = this;
      fetch(CART_ADD + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: this.variantId, quantity: 1, properties: properties }],
          sections: this.sectionsToRender(),
          sections_url: window.location.pathname
        })
      })
        .then(function (r) {
          if (!r.ok) throw new Error('add failed: ' + r.status);
          return r.json();
        })
        .then(function (data) {
          setDismissed(self.upsellKey, cartToken(), false);
          self.refresh(data);
        })
        .catch(function (err) {
          console.error('[cart-banner-upsell] add failed:', err);
          /* Auto-add is silent: on failure just retreat, no scary message.
             An explicit Undo->add failure surfaces a recoverable error. */
          if (isAuto) {
            self.hideAll();
          } else {
            self.showError();
            self.showRemoved();
          }
        })
        .finally(function () {
          self.busy = false;
          delete inFlight[lock];
        });
    }

    handleRemove() {
      if (this.busy) return;
      var token = cartToken();
      this.busy = true;
      this.hideError();
      /* Remember the opt-out first, so a re-render mid-flight doesn't re-add. */
      setDismissed(this.upsellKey, token, true);
      this.showRemoved(); /* optimistic */

      var self = this;
      fetch(CART_URL + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('cart read failed: ' + r.status);
          return r.json();
        })
        .then(function (cart) {
          var items = (cart && cart.items) || [];
          var line = null;
          for (var i = 0; i < items.length; i++) {
            var props = items[i].properties;
            if (props && props._upsell === self.upsellKey) {
              line = items[i];
              break;
            }
          }
          if (!line) {
            /* Already gone — nothing to remove, the optimistic state is right. */
            self.busy = false;
            return null;
          }
          return fetch(CART_CHANGE + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: line.key,
              quantity: 0,
              sections: self.sectionsToRender(),
              sections_url: window.location.pathname
            })
          })
            .then(function (r) {
              if (!r.ok) throw new Error('remove failed: ' + r.status);
              return r.json();
            })
            .then(function (data) {
              self.busy = false;
              self.refresh(data);
            });
        })
        .catch(function (err) {
          console.error('[cart-banner-upsell] remove failed:', err);
          /* Roll back: the line is still charged, so show it as added again. */
          setDismissed(self.upsellKey, token, false);
          self.busy = false;
          self.showError();
          self.showAdded();
        });
    }

    handleUndo() {
      if (this.busy) return;
      setDismissed(this.upsellKey, cartToken(), false);
      this.addUpsell(false);
    }

    showAdded() {
      if (this.banner) this.banner.hidden = false;
      if (this.removedRow) this.removedRow.hidden = true;
      this.style.display = '';
    }

    showRemoved() {
      if (this.banner) this.banner.hidden = true;
      if (this.showRemovedMessage && this.removedRow) {
        this.removedRow.hidden = false;
        this.style.display = '';
      } else {
        if (this.removedRow) this.removedRow.hidden = true;
        this.style.display = 'none';
      }
    }

    hideAll() {
      if (this.banner) this.banner.hidden = true;
      if (this.removedRow) this.removedRow.hidden = true;
      this.style.display = 'none';
    }

    showError() {
      if (this.errorEl) this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  customElements.define('cart-banner-upsell', CartBannerUpsell);
})();
